/**
 * UserDirectoryClient - HTTP wrapper around `GET /api/users/directory`.
 *
 * Sharing a thread requires knowing who else exists, and that list is the only
 * thing this reads: an id and a display label per account, nothing about
 * permissions or email. Kept apart from `AuthClient` because it is not an
 * `/auth/*` endpoint and answers a question about *other* people, not about
 * the current session.
 *
 * Same cookie rules as the rest of the app: `credentials: "include"`.
 */
import type { UserDirectoryEntry } from "@taskin-code/contracts";

const parseError = async (response: Response, fallback: string): Promise<string> => {
  try {
    const body = await response.json();
    if (
      body &&
      typeof body === "object" &&
      typeof (body as { error?: string }).error === "string"
    ) {
      return (body as { error: string }).error;
    }
  } catch {
    // ignore
  }
  return fallback;
};

export const UserDirectoryClient = {
  /**
   * Every account the caller may share a thread with.
   *
   * Returns an empty list on 401 rather than throwing: an install running
   * without auth has no directory, and that is not an error the UI should
   * surface — it simply has nobody to offer.
   */
  async list(): Promise<ReadonlyArray<UserDirectoryEntry>> {
    const response = await fetch("/api/users/directory", {
      method: "GET",
      credentials: "include",
    });
    if (response.status === 401 || response.status === 404) return [];
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to load the user directory"));
    }
    const body = (await response.json()) as { users?: ReadonlyArray<UserDirectoryEntry> };
    return body.users ?? [];
  },
};
